import React from "react";

import { useNavigate, useParams } from "react-router-dom";

import GuidanceLetsGoButton from "./GuidanceLetsGoButton";
import { useTodaysGuidance } from "./useTodaysGuidance";
import { useAppSelector } from "../../Store/Hooks";
import {
  CADENCE_BADGE_STYLES,
  CADENCE_ICON,
} from "../../Utils/Guidance/guidanceCadence";
import { openGuidanceDrawer } from "../../Utils/Guidance/guidanceDrawerBus";
import { isGuidanceHubActiveForUser } from "../../Utils/Guidance/guidanceHubSettings";

interface TodaysPlaybookWidgetProps {
  userRole: string | { name?: string } | null | undefined;
  className?: string;
}

/**
 * Always-visible "what should I do today" card for the home page
 * (design-note.md §4.1), placed immediately after the page's Program/
 * Organization Overview KPI section — matches the live prototype's Home (EB)
 * layout exactly. Not collapsible, not dismissible.
 */
const TodaysPlaybookWidget: React.FC<TodaysPlaybookWidgetProps> = ({
  userRole,
  className = "",
}) => {
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const navigate = useNavigate();
  const { hubEnabled, hubEnabledRoles } = useAppSelector(
    state => state.guidance
  );
  const hubActive = isGuidanceHubActiveForUser(
    hubEnabled,
    hubEnabledRoles,
    userRole
  );
  const {
    role,
    cadenceKey,
    cadenceLabel,
    playbook,
    personaContent,
    otherCadences,
    isReady,
    isLoading,
  } = useTodaysGuidance(userRole);

  if (!hubActive) return null;
  if (isLoading || !isReady) return null;

  const withOrg = (path: string) => (orgSlug ? `/${orgSlug}${path}` : path);
  const openPlaybookHub = (cadence: string) =>
    navigate(withOrg(`/guidance/playbooks?role=${role}&cadence=${cadence}`));
  const openRhythmOutcomes = () =>
    navigate(withOrg(`/guidance/playbooks?role=${role}&openRhythm=1`));

  return (
    <div
      className={`bg-white border border-gray-200 rounded-xl p-5 mb-6 guidance-widget h-full flex flex-col ${className}`.trim()}
    >
      <div className="flex-1">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center text-xl shrink-0 bg-purple-50">
                {CADENCE_ICON[cadenceKey]}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <span>{cadenceLabel} Playbook</span>
                  <span className="font-normal normal-case tracking-normal text-gray-400">
                    {playbook.time}
                  </span>
                </div>
                <h2 className="text-[17px] font-semibold text-gray-900 mt-0.5">
                  {playbook.heading}
                </h2>
                <div className="text-xs text-gray-400 mt-0.5 truncate">
                  {personaContent.personaLabel}
                  {personaContent.personaTagline
                    ? ` · ${personaContent.personaTagline}`
                    : ""}
                </div>
              </div>
            </div>

            <div className="mt-4">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                What to do today
              </div>
              <ul className="space-y-1.5">
                {playbook.what.slice(0, 3).map((line, idx) => (
                  <li key={idx} className="text-sm text-gray-700 flex gap-1.5">
                    <span className="text-[#2563eb] font-bold shrink-0">→</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <GuidanceLetsGoButton
            cadenceKey={cadenceKey}
            onClick={() => openGuidanceDrawer({ persona: role, cadenceKey })}
          />
        </div>
      </div>

      <div className="flex items-center flex-wrap gap-2.5 mt-4 pt-3 border-t border-gray-100">
        <span className="text-xs text-gray-400 whitespace-nowrap">
          Also available this week
        </span>
        {otherCadences.map(c => {
          const pillStyle =
            CADENCE_BADGE_STYLES[c.key as keyof typeof CADENCE_BADGE_STYLES];
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => openPlaybookHub(c.key)}
              className="text-xs font-medium px-2.5 py-1 rounded-full border hover:brightness-95"
              style={{
                backgroundColor: pillStyle.bg,
                color: pillStyle.text,
                borderColor: pillStyle.border,
              }}
            >
              {CADENCE_ICON[c.key as keyof typeof CADENCE_ICON]} {c.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={openRhythmOutcomes}
          className="ml-auto text-xs font-semibold text-purple-600 hover:text-purple-700 whitespace-nowrap"
        >
          Rhythm &amp; Outcomes →
        </button>
      </div>
    </div>
  );
};

export default TodaysPlaybookWidget;
